import { Badge } from "@/components/_components/badge";
import { Button, buttonVariants } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/_components/form";
import { Switch } from "@/components/_components/switch";
import { AGENT_TYPE, UC_CANDIDATE_OUTREACH } from "@/constants/constants";
import {
  CampaignRequestDto,
  useCreateCampaign,
  useGetAllLlmModels,
  useGetAllSenderPersonas,
  useGetAllSupplementData,
  useUpdateCampaign,
} from "@/lib/api/engageApi";
import { trimCampaignFormData } from "@/lib/utils/data-trimmer";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Bot,
  Clock,
  FileText,
  Hash,
  Info,
  Languages,
  Loader2,
  Mail,
  MessageSquare,
  Save,
  Settings,
  Sparkles,
  Users,
  X
} from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { textareaStyles } from "theme/components/textarea";
import { DropdownSearchCombobox } from "../DropdownSearchCombobox";
import DropdownSupplementSearch from "../DropdownSupplementSearch";
import DropdownSenderPersonaSearch from "../DropdownSenderPersonaSearch";
import { campaignDtoSchema } from "./schemas/campaignSchemas";
import { z } from "zod";
import { AuthorityGuard } from "@/components/auth/AuthorityGuard";
import { useErrorHandler } from "@/hooks/useErrorHandler";

export type CampaignFormValues = z.infer<typeof campaignDtoSchema>;

interface CreateCampaignDataFormProps {
  data?: CampaignRequestDto;
  readOnly?: boolean;
}

const CreateCampaignDataForm = ({ data, readOnly = false }: CreateCampaignDataFormProps) => {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignDtoSchema) as any,
    defaultValues: {
      id: undefined,
      agentType: "",
      name: "",
      language: "English",
      programId: "",
      senderPersonaId: undefined,
      recipientPersonaId: undefined,
      emailSubjectTemplate: "",
      llmModelId: undefined,
      temperature: 0.4,
      maxQuestions: 20,
      maxFollowupQuestions: 25,
      promptInstructions: "",
      quietHours: true,
      holidayPreferences: true,
      weekendPreferences: true,
      preflightValidation: true,
      emailEnabled: true,
      smsEnabled: true,
      whatsappEnabled: true,
    },
  });

  // Reset form with data when it becomes available
  useEffect(() => {
    if (data) {
      const formValues = {
        id: data.id,
        agentType: data.agentType || "",
        name: data.name || "",
        language: (data as any)?.language || "English",
        programId: (data as any)?.programId || "",
        senderPersonaId: data.senderPersonaId,
        recipientPersonaId: data.recipientPersonaId,
        emailSubjectTemplate: (data as any)?.emailSubjectTemplate || "",
        llmModelId: data.llmModelId,
        temperature: data.temperature !== undefined && data.temperature !== null ? data.temperature : 0.4,
        maxQuestions: (data as any)?.maxQuestions || 20,
        maxFollowupQuestions: (data as any)?.maxFollowupQuestions || 25,
        promptInstructions: (data as any)?.promptInstructions || "",
        quietHours: (data as any)?.quietHours !== undefined ? (data as any).quietHours : true,
        holidayPreferences: (data as any)?.holidayPreferences !== undefined ? (data as any).holidayPreferences : true,
        weekendPreferences: (data as any)?.weekendPreferences !== undefined ? (data as any).weekendPreferences : true,
        preflightValidation: (data as any)?.preflightValidation !== undefined ? (data as any).preflightValidation : true,
        emailEnabled: (data as any)?.emailEnabled !== undefined ? (data as any).emailEnabled : true,
        smsEnabled: (data as any)?.smsEnabled !== undefined ? (data as any).smsEnabled : true,
        whatsappEnabled: (data as any)?.whatsappEnabled !== undefined ? (data as any).whatsappEnabled : true,
      };
      form.reset(formValues);
    }
  }, [data, form]);

  const {
    mutate: createCampaign,
    isSuccess: createSuccess,
    error: createError,
    isPending: createPending,
  } = useCreateCampaign();
  const {
    mutate: updateCampaign,
    isSuccess: updateSuccess,
    error: updateError,
    isPending: updatePending,
  } = useUpdateCampaign();

  // Handle success and error states with navigation
  useEffect(() => {
    if (createSuccess) {
      toast.success("Campaign created successfully!");
      navigate(-1); // Navigate to previous page
    }
  }, [createSuccess, navigate]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("Campaign updated successfully!");
      navigate(-1); // Navigate to previous page
    }
  }, [updateSuccess, navigate]);

  useEffect(() => {
    if (createError) {
      handleError(createError);
    }
  }, [createError, handleError]);

  useEffect(() => {
    if (updateError) {
      handleError(updateError);
    }
  }, [updateError, handleError]);

  function onSubmit(values: CampaignFormValues) {
    // Trim all keys and values before sending to backend
    const trimmedValues = trimCampaignFormData(values);

    if (data?.id) {
      updateCampaign({ data: trimmedValues });
    } else {
      createCampaign({ data: trimmedValues });
    }
    // Don't reset form since we'll navigate away on success
  }

  const { data: llmModels } = useGetAllLlmModels();

  // Fetch sender persona data from the dedicated endpoint
  const selectedAgentType = form.watch("agentType");
  const { data: senderPersonaData } = useGetAllSenderPersonas(
    selectedAgentType ? { agentType: selectedAgentType } : undefined,
    {
      request: {
        headers: {
          isPaginated: false,
        },
      },
    }
  );

  // Fetch recipient persona data to get descriptions
  const { data: recipientPersonaData } = useGetAllSupplementData(
    { type: "Recipient Persona", status: true },
    {
      request: {
        headers: {
          isPaginated: false,
        },
      },
    }
  );

  // Fetch languages data
  const { data: languagesData } = useGetAllSupplementData(
    { type: "Language", status: true },
    {
      request: {
        headers: {
          isPaginated: false,
        },
      },
    }
  );

  // Get the selected sender persona's description
  const selectedSenderPersona = senderPersonaData?.data?.find(
    (item: any) => item.id?.toString() === form.watch("senderPersonaId")?.toString()
  );

  // Get the selected recipient persona's description
  const selectedRecipientPersona = recipientPersonaData?.data?.find(
    (item) => item.id?.toString() === form.watch("recipientPersonaId")?.toString()
  );

  // Check if any operation is pending
  const isProcessing = createPending || updatePending;

  // Clear sender persona when agent type changes (only in create mode)
  useEffect(() => {
    if (!data?.id && selectedAgentType) {
      form.setValue("senderPersonaId", undefined);
    }
  }, [selectedAgentType, data?.id, form]);

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Campaign Overview Section */}
          <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-5">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                  <Settings className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    Campaign Configuration
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Set up the basic campaign parameters and settings
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* Agent Type */}
                <FormField
                  control={form.control}
                  name="agentType"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <FileText className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Agent Type</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <FormControl>
                        {data?.id ? (
                          <div className="flex items-center h-11 px-4 py-2.5 border border-border bg-muted rounded-lg text-muted-foreground text-sm">
                            {field.value}
                          </div>
                        ) : (
                          <DropdownSearchCombobox
                            options={AGENT_TYPE?.map((item) => ({
                              label: item,
                              value: item,
                            }))}
                            id="agentType"
                            placeholder="Select Agent Type..."
                            {...field}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Campaign */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Sparkles className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Campaign</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <FormControl>
                        {data?.id ? (
                          <div className="flex items-center h-11 px-4 py-2.5 border border-border bg-muted rounded-lg text-muted-foreground text-sm">
                            {field.value}
                          </div>
                        ) : (
                          <input
                            placeholder="Enter a descriptive campaign name..."
                            className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                            {...field}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Language */}
                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Languages className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Language</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <FormControl>
                        {data?.id ? (
                          <div className="flex items-center h-11 px-4 py-2.5 border border-border bg-muted rounded-lg text-muted-foreground text-sm">
                            {field.value}
                          </div>
                        ) : (
                          <DropdownSearchCombobox
                            options={
                              languagesData?.data?.map((item) => ({
                                label: item.name,
                                value: item.name,
                              })) ?? []
                            }
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select language..."
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Program ID */}
                <FormField
                  control={form.control}
                  name="programId"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-muted/50 rounded-md">
                          <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <span>Program ID</span>
                        <Badge variant="secondary" className="text-xs px-2 py-0.5 font-medium">Optional</Badge>
                      </FormLabel>
                      <FormControl>
                        <input
                          placeholder="Enter program ID..."
                          className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Campaign Code - Only visible in Edit mode */}
                {data?.id && (data as any)?.code && (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                      <div className="p-1.5 bg-muted/50 rounded-md">
                        <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <span>Campaign Code</span>
                    </FormLabel>
                    <div className="flex items-center h-11 px-4 py-2.5 border border-border bg-muted rounded-lg text-muted-foreground font-mono text-sm">
                      {(data as any)?.code}
                    </div>
                  </FormItem>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Personas Section */}
          <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-5">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    Persona Configuration
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Define sender and recipient personas for personalized interactions
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* Sender Persona */}
                <FormField
                  control={form.control}
                  name="senderPersonaId"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Users className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Sender Persona</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <div className="flex items-center gap-3">
                        <FormControl className="flex-1">
                          <DropdownSenderPersonaSearch
                            placeholder="Select sender persona..."
                            value={field?.value?.toString() ?? ""}
                            agentType={selectedAgentType}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        {selectedSenderPersona && (
                          <div className="relative group">
                            <div
                              title={(selectedSenderPersona as any).description || "No description available"}
                              className="p-2 hover:bg-primary/10 rounded-lg transition-colors cursor-help group/info"
                            >
                              <Info className="h-4 w-4 text-muted-foreground group-hover/info:text-primary transition-colors" />
                            </div>
                            <div className="absolute right-0 top-12 z-50 invisible group-hover:visible bg-popover text-popover-foreground text-xs rounded-lg py-2 px-3 max-w-xs whitespace-normal shadow-xl border border-border">
                              {(selectedSenderPersona as any).description || "No description available"}
                              <div className="absolute -top-1 right-3 w-2 h-2 bg-popover rotate-45 border-l border-t border-border"></div>
                            </div>
                          </div>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Recipient Persona */}
                <FormField
                  control={form.control}
                  name="recipientPersonaId"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Users className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Recipient Persona</span>
                        <Badge variant="secondary" className="text-xs px-2 py-0.5 font-medium">Optional</Badge>
                      </FormLabel>
                      <div className="flex items-center gap-3">
                        <FormControl className="flex-1">
                          <DropdownSupplementSearch
                            placeholder="Select recipient persona (optional)..."
                            type="Recipient Persona"
                            value={field?.value?.toString() ?? ""}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        {field.value && (
                          <button
                            type="button"
                            onClick={() => field.onChange("")}
                            className="p-2 hover:bg-destructive/10 rounded-lg transition-all duration-200 group hover:scale-110"
                            title="Clear selection"
                          >
                            <X className="h-4 w-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                          </button>
                        )}
                        {selectedRecipientPersona && (
                          <div className="relative group">
                            <div
                              title={selectedRecipientPersona.descr || "No description available"}
                              className="p-2 hover:bg-primary/10 rounded-lg transition-colors cursor-help group/info"
                            >
                              <Info className="h-4 w-4 text-muted-foreground group-hover/info:text-primary transition-colors" />
                            </div>
                            <div className="absolute right-0 top-12 z-50 invisible group-hover:visible bg-popover text-popover-foreground text-xs rounded-lg py-2 px-3 max-w-xs whitespace-normal shadow-xl border border-border">
                              {selectedRecipientPersona.descr || "No description available"}
                              <div className="absolute -top-1 right-3 w-2 h-2 bg-popover rotate-45 border-l border-t border-border"></div>
                            </div>
                          </div>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email Subject Template - Full Width */}
              <FormField
                control={form.control}
                name="emailSubjectTemplate"
                render={({ field }) => (
                    <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                      <div className="p-1.5 bg-primary/5 rounded-md">
                        <MessageSquare className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <span>Email Subject Template</span>
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 font-medium">Optional</Badge>
                    </FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Enter email subject template..."
                        className={cn(textareaStyles, "min-h-[80px] resize-y")}
                        maxLength={500}
                        {...field}
                      />
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-muted-foreground">
                        Template for email subject lines
                      </p>
                      <p className="text-xs text-muted-foreground/70">
                        {field.value?.length || 0}/500 characters
                      </p>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* AI & Settings Section */}
          <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-5">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    AI Model & Limits
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Configure the AI model and conversation parameters
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* LLM Model */}
                <FormField
                  control={form.control}
                  name="llmModelId"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Bot className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>AI Model</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <FormControl>
                        <DropdownSearchCombobox
                          options={
                            llmModels?.data
                              ?.filter((item) => item.status && item.name)
                              .map((item) => ({
                                label: `${item.provider} - ${item.name}`,
                                value: item?.id?.toString(),
                              })) ?? []
                          }
                          value={field?.value?.toString() ?? ""}
                          onChange={field.onChange}
                          placeholder="Select AI model..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Temperature */}
                <FormField
                  control={form.control}
                  name="temperature"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/5 rounded-md">
                          <Hash className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span>Temperature</span>
                        <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                      </FormLabel>
                      <FormControl>
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.1"
                          placeholder="e.g., 0.4"
                          required
                          className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? undefined : Number.parseFloat(value));
                          }}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Temperature range: 0.0 to 1.0 (1 decimal place)
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Maximum Questions and Follow-up Questions - Only for CANDIDATE_OUTREACH */}
              {form.watch("agentType") === UC_CANDIDATE_OUTREACH && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Maximum Questions */}
                  <FormField
                    control={form.control}
                    name="maxQuestions"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                          <div className="p-1.5 bg-primary/5 rounded-md">
                            <Hash className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>Maximum Questions</span>
                          <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                        </FormLabel>
                        <FormControl>
                          <input
                            type="number"
                            min="1"
                            placeholder="10"
                            required
                            className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? undefined : Number.parseInt(value));
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Maximum number of questions to ask (Default: 10)
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Maximum Follow-up Questions */}
                  <FormField
                    control={form.control}
                    name="maxFollowupQuestions"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                          <div className="p-1.5 bg-primary/5 rounded-md">
                            <Hash className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>Maximum Follow-ups</span>
                          <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                        </FormLabel>
                        <FormControl>
                          <input
                            type="number"
                            min="1"
                            placeholder="10"
                            required
                            className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? undefined : Number.parseInt(value));
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Maximum number of follow-ups (Default: 20)
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Prompt Instructions - Full Width */}
              <FormField
                control={form.control}
                name="promptInstructions"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                      <div className="p-1.5 bg-primary/5 rounded-md">
                        <MessageSquare className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <span>Prompt Instructions</span>
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 font-medium">Optional</Badge>
                    </FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Enter specific instructions for the AI model to follow during conversations..."
                        className={cn(textareaStyles, "min-h-[120px] resize-y")}
                        maxLength={2000}
                        {...field}
                      />
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-muted-foreground">
                        Provide specific guidelines, tone, or behavior instructions for the AI
                      </p>
                      <p className="text-xs text-slate-400">
                        {field.value?.length || 0}/2000 characters
                      </p>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Quiet Hours Section */}
          <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-5">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    Quiet Hours
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Configure timing and scheduling preferences
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Enable Quiet Hours */}
                <FormField
                  control={form.control}
                  name="quietHours"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          Enable Quiet Hours
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Respect quiet hours for communications
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Holiday Preferences */}
                <FormField
                  control={form.control}
                  name="holidayPreferences"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          Holiday Preferences
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Avoid sending during holidays
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Weekend Preferences */}
                <FormField
                  control={form.control}
                  name="weekendPreferences"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          Weekend Preferences
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Avoid sending during weekends
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Pre-flight Validation */}
                <FormField
                  control={form.control}
                  name="preflightValidation"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          Pre-flight Validation
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Validate before sending
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Communication Channel Section */}
          <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-5">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                    Communication Channel
                    <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">At least 1 required</Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Enable or disable communication channels (at least one must be enabled)
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Email Enabled */}
                <FormField
                  control={form.control}
                  name="emailEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          Email Enabled
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Enable email channel
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* SMS Enabled */}
                <FormField
                  control={form.control}
                  name="smsEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          SMS Enabled
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Enable SMS channel
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* WhatsApp Enabled */}
                <FormField
                  control={form.control}
                  name="whatsappEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/30 p-4 space-y-0 hover:bg-muted/50 transition-colors">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-semibold text-foreground">
                          WhatsApp Enabled
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Enable WhatsApp channel
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          {!readOnly && (
            <div className="flex flex-col sm:flex-row justify-end gap-4 pt-2 pb-4">
              <AuthorityGuard authority="ADMIN_CAMPAIGN_MGMT_UPDATE">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isProcessing}
                  className="bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-primary-foreground shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 min-w-[160px] font-semibold"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {isProcessing
                    ? (data?.id ? 'Updating...' : 'Creating...')
                    : (data?.id ? 'Update Campaign' : 'Create Campaign')
                  }
                </Button>
              </AuthorityGuard>
              <Link
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "min-w-[160px] shadow-sm hover:shadow-md hover:bg-accent transition-all duration-200",
                  isProcessing && "pointer-events-none opacity-50 cursor-not-allowed"
                )}
                to="/campaign-management"
                onClick={(e) => {
                  if (isProcessing) {
                    e.preventDefault();
                  }
                }}
              >
                Cancel
              </Link>
            </div>
          )}
          {readOnly && (
            <div className="flex justify-end pt-2 pb-4">
              <Link
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "min-w-[160px] shadow-sm hover:shadow-md hover:bg-accent transition-all duration-200"
                )}
                to="/campaign-management"
              >
                Back to Campaigns
              </Link>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default CreateCampaignDataForm;